import { isEmpty } from 'lodash';
import {
  COMPOUNDING_PERIOD_ALIAS,
  DIVIDEND_RATE_ALIAS,
  LPL_EFFECTIVE_DATE_ALIAS,
  LPL_EFFECTIVE_DATE_ROW_NUMBER,
} from 'common/constants/cap-table';
import { LiqPrefCell } from 'pages/CapTable/cap-table/components/liquidation-preference-ledger/liq-pref-ledger-table-config/liqPrefLedgerValidations.props';
import {
  COMPOUNDING_PERIOD_IS_REQUIRED_FEEDBACK,
  COMPOUNDING_PERIOD_MIN_VALUE_FEEDBACK,
  DIVIDEND_RATE_IS_REQUIRED_FEEDBACK,
  PREVIOUS_EFFECTIVE_DATE_FEEDBACK,
} from 'pages/CapTable/cap-table/data/validationsConstants';
import { validateEffectiveDateWithInvestmentDate } from 'pages/CapTable/cap-table/utilities';
import { getColumnIndex, getColumnLetter, getObjectValue } from 'utilities';
import { HAS_COMPOUNDING_ROW, HAS_DIVIDENDS_ROW } from './constants';
import { LiqPrefLedgerValidationsParams } from './liqPrefLedgerValidations.props';

const FIRST_COLUMN_LETTER = 'A';

const validateDividendRate = ({
  cell,
  addFeedbackFromCell,
  removeFeedbackFromCell,
  parsedColumns,
  tableData,
}: LiqPrefLedgerValidationsParams) => {
  const { value, columnLegend } = cell;
  const { isUsingByExitDate } = getObjectValue(tableData);
  const rowNumber = isUsingByExitDate ? HAS_DIVIDENDS_ROW + 1 : HAS_DIVIDENDS_ROW;
  const hasDividendsKey = `${columnLegend as string}${rowNumber}`;
  const hasDividendsEnabled = parsedColumns[hasDividendsKey].value;
  if (Number(hasDividendsEnabled) && isEmpty(value)) {
    addFeedbackFromCell?.(cell, DIVIDEND_RATE_IS_REQUIRED_FEEDBACK);
  } else if (Number(hasDividendsEnabled)) {
    removeFeedbackFromCell?.(cell, DIVIDEND_RATE_IS_REQUIRED_FEEDBACK);
  }
};

const validateCompoundingPeriod = ({
  cell,
  addFeedbackFromCell,
  removeFeedbackFromCell,
  parsedColumns,
  tableData,
}: LiqPrefLedgerValidationsParams) => {
  const { value, columnLegend } = cell;
  const { isUsingByExitDate } = getObjectValue(tableData);
  const rowNumber = isUsingByExitDate ? HAS_COMPOUNDING_ROW + 1 : HAS_COMPOUNDING_ROW;
  const hasCompoundingKey = `${columnLegend as string}${rowNumber}`;
  const hasCompoundingEnabled = parsedColumns[hasCompoundingKey]?.value;
  if (Number(hasCompoundingEnabled)) {
    if (!value) {
      addFeedbackFromCell?.(cell, COMPOUNDING_PERIOD_IS_REQUIRED_FEEDBACK);
    } else {
      removeFeedbackFromCell?.(cell, COMPOUNDING_PERIOD_IS_REQUIRED_FEEDBACK);
    }
    if (Number(value) < 1) {
      addFeedbackFromCell?.(cell, COMPOUNDING_PERIOD_MIN_VALUE_FEEDBACK);
    } else {
      removeFeedbackFromCell?.(cell, COMPOUNDING_PERIOD_MIN_VALUE_FEEDBACK);
    }
  }
};

const validateEffectiveDate = ({
  cell,
  addFeedbackFromCell,
  removeFeedbackFromCell,
  parsedColumns,
  tableData,
}: LiqPrefLedgerValidationsParams) => {
  const { currentSecurity } = getObjectValue(tableData);
  const investmentDate = currentSecurity?.investment_date;
  const { value, columnLegend } = cell;
  if (columnLegend !== FIRST_COLUMN_LETTER) {
    // validate that the previous cell effective date is lower than the current cell effective date
    const currentColumnNumber = getColumnIndex(columnLegend);
    const referencedColumnLegend = getColumnLetter((currentColumnNumber as number) - 1);
    // need to fix the references
    const referencedCell = parsedColumns[`${referencedColumnLegend}${LPL_EFFECTIVE_DATE_ROW_NUMBER}`];
    if (referencedCell?.value) {
      const isValid = new Date(value as string) > new Date(referencedCell.value as string);
      if (!isValid) {
        addFeedbackFromCell?.(cell, PREVIOUS_EFFECTIVE_DATE_FEEDBACK);
      } else {
        removeFeedbackFromCell?.(cell, PREVIOUS_EFFECTIVE_DATE_FEEDBACK);
      }
    }
  }
  validateEffectiveDateWithInvestmentDate<LiqPrefCell>({
    cell,
    investmentDate,
    addFeedbackFromCell,
    removeFeedbackFromCell,
  });
};

const validations = (params: LiqPrefLedgerValidationsParams) => {
  const { cell, parsedColumns, addFeedbackFromCell, removeFeedbackFromCell, tableData } = params;
  const { alias } = cell;
  // cumulative dividends true adds dividends rate but 0 is valid
  if (alias === DIVIDEND_RATE_ALIAS) {
    validateDividendRate({ cell, addFeedbackFromCell, removeFeedbackFromCell, parsedColumns, tableData });
  }
  if (alias === COMPOUNDING_PERIOD_ALIAS) {
    validateCompoundingPeriod({ cell, addFeedbackFromCell, removeFeedbackFromCell, parsedColumns, tableData });
  }
  if (alias === LPL_EFFECTIVE_DATE_ALIAS) {
    validateEffectiveDate({ cell, addFeedbackFromCell, removeFeedbackFromCell, parsedColumns, tableData });
  }
};

export default validations;
